/**
 * UpdateBanner — pokazuje się gdy vite-plugin-pwa wykryje nową wersję SW.
 * Buttony: "Odśwież" (skipWaiting + reload) / "Później" (zamyka banner do
 * następnego pollingu). Co 60s polling registration.update() — sprawdza czy
 * jest nowy SW na serwerze. Po deployu user nie musi nic robić — sam dostanie
 * banner w ciągu minuty.
 */
import { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Icons } from './Icons';
import { haptic } from '../lib/utils';
import { hardReload } from '../lib/hardReload';

const POLL_INTERVAL_MS = 60_000;

export function UpdateBanner() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Polling co minutę — Firebase Hosting serwuje świeży SW (no-cache header),
      // więc registration.update() ściąga aktualny.
      setInterval(() => {
        registration.update().catch(() => {
          /* sieć padła — spróbujemy znowu za minutę */
        });
      }, POLL_INTERVAL_MS);
    },
    onRegisterError(err) {
      console.error('[PWA] SW register error:', err);
    },
  });

  const [updating, setUpdating] = useState(false);

  if (!needRefresh) return null;

  const handleUpdate = async () => {
    haptic('medium');
    setUpdating(true);
    // Strategia: trzy warstwy fallbacków, żeby ZAWSZE się odświeżyło.
    //  1) updateServiceWorker(true) — vite-plugin-pwa wysyła SKIP_WAITING +
    //     reload na controllerchange. Działa w 90% przypadków.
    //  2) Po 1500ms jeśli nadal jesteśmy tu — hardReload (unregister SW +
    //     caches.delete + window.location.replace z timestamp).
    //  3) Listener na controllerchange + visibilitychange jako safety net.
    const fallbackTimer = window.setTimeout(() => {
      hardReload();
    }, 1500);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          window.clearTimeout(fallbackTimer);
          window.location.reload();
        },
        { once: true },
      );
    }

    try {
      await updateServiceWorker(true);
    } catch (err) {
      console.warn('[UpdateBanner] updateServiceWorker failed, fallback:', err);
      window.clearTimeout(fallbackTimer);
      hardReload();
    }
  };

  const handleLater = () => {
    haptic('light');
    setNeedRefresh(false);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6 lg:left-auto lg:right-6 lg:w-96 z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-500/15 flex items-center justify-center text-sky-600 dark:text-sky-400 shrink-0">
            <Icons.Sparkles size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 dark:text-white text-sm">
              Dostępna nowa wersja
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Odśwież żeby załadować najnowsze poprawki
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleLater}
            disabled={updating}
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700/40 transition disabled:opacity-50"
          >
            Później
          </button>
          <button
            type="button"
            onClick={handleUpdate}
            disabled={updating}
            className="flex-[1.5] px-3 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition flex items-center justify-center gap-1.5 disabled:opacity-70"
          >
            <Icons.Refresh size={14} strokeWidth={2.5} className={updating ? 'animate-spin' : ''} />
            {updating ? 'Odświeżam...' : 'Odśwież'}
          </button>
        </div>
      </div>
    </div>
  );
}
